// @givmo/board — Board's words in the shared "Confirm it's you" sheet (CBG-SEC2 SEC2-WEB).
//
// The sheet is Account Security's (D-AS-17/19/21) and only renders these: Board's rows, keyed by the
// row Board's refusal names (`board_director_signing_required` → one row per act or read, through
// the gate's code-to-row map; Account Security, 2026-10-02 22:43 PDT: "B2a's 'Confirm it's you to
// keep signing' and the B2d/B2d-google lines are Board's rows in `BOARD_SIGNING_COPY`").
//
// The consent signature's words are the approved frames' (Jonathan's mockups, D-CBG-18: B2a, B2d and
// B2d-google as drawn by Account Security). The other five rows follow its pattern, and Jonathan
// approved them in his copy round (2026-10-05 20:25 PDT, D-CBG-18), which also gave the
// executive-session row its line. A seventh, for a board that asked before anything opened, went
// with that option (D-CBG-20). No row says "verify": the Board surfaces avoid that root (D-AS-29:
// "Confirm it's you" everywhere).
//
// THE TWO ARMS' LINES (the row fields of Account Security's AS-WEB10). `description` is the passkey
// arm's: the window, then the act's own line (B2a). `reSignInDescription` is the person's own way
// back in: the window, then what to do — "Enter your password, then the code from your authenticator
// app." (B2d), "Continue with Google, then enter the code from your authenticator app." (B2d-google).
// The buttons are the sheet's own: "Continue" on a credential step a code follows, "Confirm" on the
// code step (B1c) and on any step that completes the confirmation.
//
// WHAT A LOST FACTOR MEANS HERE (Account Security's `lostLines`, AS-WEB18). "Lost your
// authenticator?" and "Lost your passkey?" open the account's own views; Board's rows say what the
// loss means for the act or read at hand. On the act rows the authenticator's three lines are the
// approved B2d-lost frame's, verbatim. Everything else the orchestrator set (2026-10-03 14:10,
// 15:13, 15:20 and 16:57 PDT), and Jonathan approved 2026-10-03 17:31 (D-CBG-18 note):
//   - the act rows' result after a recovery code, made of two approved sentences (D-AS-35 #15, and
//     B2d-lost's own helper). A recovery code ends every session, so the director signs in again
//     first;
//   - the passkey's line: B2d-lost's line with "them", since the passkey reset is "Reset your
//     passkeys" (the orchestrator, 15:20 PDT);
//   - every read row's line: reading there needs the window too, so nothing goes on meanwhile.

import type {
  StepUpCopyRow,
  StepUpCopyTable,
  StepUpLostFactor,
  StepUpLostLines,
  StepUpReSignInArm,
} from '@givmo/step-up';
import type { BoardSigningAct } from './director-wire';

/** The rows: one per signature-class act, and one for the read the window gates. */
export const BOARD_SIGNING_ROWS = {
  consent_signature: 'board_consent_signature',
  consent_signature_withdrawal: 'board_consent_withdrawal',
  minutes_signature: 'board_minutes_signature',
  // CBG-RC10: a director who was in a closed session signing to approve its minutes.
  minutes_approval_signature: 'board_minutes_approval_signature',
  record_acknowledgment: 'board_record_acknowledgment',
  conflict_disclosure: 'board_conflict_disclosure',
  // CBG-YR2-WEB2: the year's conflict-of-interest answers, signed.
  coi_questionnaire: 'board_coi_questionnaire',
  open_executive: 'board_open_executive',
  // CBG-FIX106 (the FIX104 copy page's row 4.11): the same read, for a seat for someone who isn't
  // on the board, opening its committee's closed-session minutes.
  open_executive_committee: 'board_open_executive_committee',
  // CBG-FIX74: "Open" on minutes waiting for the person's signature while the window is shut.
  minutes_waiting: 'board_minutes_waiting',
} as const;

export type BoardSigningRow = (typeof BOARD_SIGNING_ROWS)[keyof typeof BOARD_SIGNING_ROWS];

const KEEP_SIGNING = 'Confirm it’s you to keep signing';
const SIXTY_MINUTES = 'Then you can sign for 60 minutes on this device.';
const UNAVAILABLE = 'Signing can’t open here. Sign out and back in, then try again.';

/** A day, kept on one line wherever the sentence wraps (B2d-lost's own non-breaking space). */
const DAY = '24\u00a0hours';

type LostLines = Partial<Record<StepUpLostFactor, StepUpLostLines>>;

/**
 * Approved (B2d-lost): what goes on while a factor is lost, and what waits. Changed in CBG-FIX106
 * (the FIX104 copy page's row 4.12, approved 2026-10-17 17:42 PDT): "an officer", true of every
 * seat, where it said "an officer of your board" to a committee member who isn't on it too.
 */
const READ_AND_REPLY =
  'You can still read and reply. Signing waits until you’ve replaced it, and an officer can record your signature for you meanwhile.';

/**
 * The passkey reset's, approved 2026-10-03 17:31 (D-CBG-18 note): the same, for "Reset your
 * passkeys"; changed with it in CBG-FIX106 (row 4.12).
 */
const READ_AND_REPLY_PASSKEYS =
  'You can still read and reply. Signing waits until you’ve replaced them, and an officer can record your signature for you meanwhile.';

/** The act rows' (see the header). */
const ACT_LOST: LostLines = {
  authenticator: {
    line: READ_AND_REPLY,
    recoveryCode: `Removes the lost authenticator right away. You can sign ${DAY} later, with a new passkey or authenticator.`,
    emailReset: `No recovery codes? The reset takes ${DAY}, and we tell you at each step. You can sign again once it’s done, with a new passkey or authenticator.`,
    recoveryCodeResult: `You’ll be asked to sign in again, without a code. You can sign ${DAY} later, with a new passkey or authenticator.`,
  },
  passkey: { line: READ_AND_REPLY_PASSKEYS },
};

/**
 * The read rows', approved 2026-10-03 17:31 (D-CBG-18 note): what waits is the reading itself.
 * "This" is the material and "it" the lost factor, never the other way round (the orchestrator,
 * 16:57 PDT).
 */
const STAYS_CLOSED = 'This stays closed until you’ve replaced it.';
const STAYS_CLOSED_PASSKEYS = 'This stays closed until you’ve replaced them.';
const READ_LOST: LostLines = {
  authenticator: {
    line: STAYS_CLOSED,
    recoveryCode: `Removes the lost authenticator right away. You can open this ${DAY} later, with a new passkey or authenticator.`,
    emailReset: `No recovery codes? The reset takes ${DAY}, and we tell you at each step. You can open this again once it’s done, with a new passkey or authenticator.`,
    recoveryCodeResult: `You’ll be asked to sign in again, without a code. You can open this ${DAY} later, with a new passkey or authenticator.`,
  },
  passkey: { line: STAYS_CLOSED_PASSKEYS },
};

/**
 * What the person's own way back in asks of them (B2d, B2d-google): their password or their
 * provider's window, then the code when one follows.
 */
function reSignInLine(arm: StepUpReSignInArm): string {
  if (arm.way === 'password') {
    return arm.codeFollows
      ? 'Enter your password, then the code from your authenticator app.'
      : 'Enter your password.';
  }
  const provider = `Continue with ${arm.providerLabel ?? 'your account'}`;
  return arm.codeFollows
    ? `${provider}, then enter the code from your authenticator app.`
    : `${provider}.`;
}

function act(line: string, consequence: string): StepUpCopyRow {
  return {
    title: KEEP_SIGNING,
    description: () => `${SIXTY_MINUTES} ${line}`,
    reSignInDescription: (_context, arm) => `${SIXTY_MINUTES} ${reSignInLine(arm)}`,
    consequence,
    challengeTitle: KEEP_SIGNING,
    challengeAction: 'keep signing',
    unavailable: UNAVAILABLE,
    lostLines: ACT_LOST,
  };
}

/** A read the window gates: its own line on the passkey arm, then the way in on the other. */
function read(title: string, line: string, challengeAction: string): StepUpCopyRow {
  return {
    title,
    description: () => line,
    reSignInDescription: (_context, arm) => `${line} ${reSignInLine(arm)}`,
    challengeTitle: title,
    challengeAction,
    unavailable: UNAVAILABLE,
    lostLines: READ_LOST,
  };
}

export const BOARD_SIGNING_COPY: StepUpCopyTable = {
  // Approved (B2a): the consent's own line.
  [BOARD_SIGNING_ROWS.consent_signature]: act(
    'This consent is signed as soon as you confirm.',
    'Nothing is signed until you confirm.',
  ),
  [BOARD_SIGNING_ROWS.consent_signature_withdrawal]: act(
    'Your signature is withdrawn as soon as you confirm.',
    'Nothing changes until you confirm.',
  ),
  [BOARD_SIGNING_ROWS.minutes_signature]: act(
    'These minutes are signed as soon as you confirm.',
    'Nothing is signed until you confirm.',
  ),
  // CBG-RC10: the act's own line, on the pattern of the others, approved in copy round
  // CBG-RC10-COPY1; its consequence is the minutes signature's, approved.
  [BOARD_SIGNING_ROWS.minutes_approval_signature]: act(
    'Your signature approving these minutes is recorded as soon as you confirm.',
    'Nothing is signed until you confirm.',
  ),
  [BOARD_SIGNING_ROWS.record_acknowledgment]: act(
    'This is recorded as received as soon as you confirm.',
    'Nothing is recorded until you confirm.',
  ),
  [BOARD_SIGNING_ROWS.conflict_disclosure]: act(
    'Your disclosure is recorded as soon as you confirm.',
    'Nothing is recorded until you confirm.',
  ),
  // The derivation's §2.5, "as the other acts'" (the confirm sheet is not drawn in the frames);
  // approved in CBG-YR2-COPY1.
  [BOARD_SIGNING_ROWS.coi_questionnaire]: act(
    'Your answers are signed as soon as you confirm.',
    'Nothing is signed until you confirm.',
  ),
  [BOARD_SIGNING_ROWS.open_executive]: read(
    'Confirm it’s you to open this',
    'Your board asks for this before executive-session material opens. Then you can open it, and sign, for 60 minutes on this device.',
    'open this',
  ),
  // CBG-FIX106 (the FIX104 copy page's row 4.11, approved 2026-10-17 17:42 PDT): the same row for
  // a committee member who isn't on the board, its committee's closed-session minutes.
  [BOARD_SIGNING_ROWS.open_executive_committee]: read(
    'Confirm it’s you to open this',
    'Your committee asks for this before executive-session material opens. Then you can open it, and sign, for 60 minutes on this device.',
    'open this',
  ),
  // CBG-FIX74 (design-225's copy page, row 1.3, approved 2026-10-17 22:51 PDT; frame b2): every line
  // an approved one. The act rows' title and their "Then you can sign for 60 minutes on this device."
  // alone, with no act line and no consequence, since confirming here signs nothing; their way back
  // in, unavailable line and lost-factor lines. Never the executive-session row's line: it would name
  // a closed session on a page the shut window keeps from saying so.
  [BOARD_SIGNING_ROWS.minutes_waiting]: {
    title: KEEP_SIGNING,
    description: () => SIXTY_MINUTES,
    reSignInDescription: (_context, arm) => `${SIXTY_MINUTES} ${reSignInLine(arm)}`,
    challengeTitle: KEEP_SIGNING,
    challengeAction: 'keep signing',
    unavailable: UNAVAILABLE,
    lostLines: ACT_LOST,
  },
};

/**
 * The row a refused read of executive-session material opens, for the seat it is read as: a seat
 * for someone who isn't on the board reads only its committees' (CBG-FIX106, row 4.11).
 */
export function openExecutiveRowFor(committeeOnly: boolean): BoardSigningRow {
  return committeeOnly
    ? BOARD_SIGNING_ROWS.open_executive_committee
    : BOARD_SIGNING_ROWS.open_executive;
}

/** The row an act's refusal opens. */
export function signingRowFor(act: BoardSigningAct): BoardSigningRow {
  return BOARD_SIGNING_ROWS[act];
}
